import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useDataPersistence } from './useDataPersistence';
/**
 * Navigation-Aware Data Refresh Hook
 *
 * Automatically refreshes data when navigating to key pages to ensure
 * users always see the latest data from the database.
 */
export const useNavigationRefresh = () => {
    const location = useLocation();
    const { isAuthenticated } = useSelector((state) => state.auth);
    const { refreshUserData, refreshNotes, refreshLessons, refreshSchedule, refreshAnalytics, refreshGamification, refreshMessaging, refreshQuizzes, refreshRubrics, refreshPayments, refreshMeetings, refreshClassrooms, refreshEngagement, refreshProgress, refreshResourceLibrary } = useDataPersistence();
    useEffect(() => {
        const pathname = location.pathname;
        // Only refresh data if user is authenticated
        if (!isAuthenticated) {
            return;
        }
        // Admin shell manages its own data; loose includes('/rubric'|'/settings'|'/dashboard')
        // would false-match /admin/* and trigger student/tutor refreshes + auth.loading flickers
        if (pathname.startsWith('/admin')) {
            return;
        }
        // Refresh data when navigating to key pages (force on settings/profile so edits are reflected)
        if (pathname.includes('/settings') || pathname.includes('/profile')) {
            refreshUserData(true);
        }
        if (pathname.includes('/notes') || pathname.includes('/notes-dashboard')) {
            refreshNotes();
        }
        if (pathname.includes('/lessons') || pathname.includes('/lesson')) {
            refreshLessons();
        }
        if (pathname.includes('/schedule') || pathname.includes('/availability')) {
            refreshSchedule();
        }
        if (pathname.includes('/analytics') || pathname.includes('/dashboard')) {
            refreshAnalytics();
        }
        if (pathname.includes('/gamification') || pathname.includes('/achievements')) {
            refreshGamification();
        }
        // New feature navigation refresh
        if (pathname.includes('/messages') || pathname.includes('/chat') || pathname.includes('/conversations')) {
            refreshMessaging();
        }
        if (pathname.includes('/whiteboard') || pathname.includes('/drawing') || pathname.includes('/collaboration')) {
            // Whiteboard data is typically real-time, but we can refresh related data
            refreshMeetings();
        }
        // Match /quiz/... or trailing /quiz, not e.g. .../quiz-management (substring "/quiz" matched that path)
        if (/\/quiz(\/|$)/.test(pathname) || pathname.includes('/assessment') || pathname.includes('/test')) {
            refreshQuizzes();
        }
        if (pathname.includes('/rubric') || pathname.includes('/grading') || pathname.includes('/evaluation')) {
            refreshRubrics();
        }
        if (pathname.includes('/payment') || pathname.includes('/billing') || pathname.includes('/transactions')) {
            refreshPayments();
        }
        if (pathname.includes('/meeting') || pathname.includes('/video') || pathname.includes('/session')) {
            refreshMeetings();
        }
        if (pathname.includes('/classroom') || pathname.includes('/course') || pathname.includes('/class')) {
            refreshClassrooms();
        }
        if (pathname.includes('/engagement') || pathname.includes('/activity') || pathname.includes('/participation')) {
            refreshEngagement();
        }
        if (pathname.includes('/progress') || pathname.includes('/tracking') || pathname.includes('/milestones')) {
            refreshProgress();
        }
        if (pathname.includes('/resources') || pathname.includes('/library') || pathname.includes('/materials')) {
            refreshResourceLibrary();
        }
        // Special case: Home dashboard - refresh all relevant data
        if (pathname === '/' || pathname === '/dashboard' || pathname === '/home') {
            // Refresh core data for dashboard
            Promise.all([
                refreshUserData(),
                refreshNotes(),
                refreshAnalytics(),
                refreshGamification(),
                refreshMessaging(),
                refreshPayments()
            ]).catch(error => {
                console.error('❌ Error refreshing dashboard data:', error);
            });
        }
    }, [
        isAuthenticated,
        location.pathname,
        refreshUserData,
        refreshNotes,
        refreshLessons,
        refreshSchedule,
        refreshAnalytics,
        refreshGamification,
        refreshMessaging,
        refreshQuizzes,
        refreshRubrics,
        refreshPayments,
        refreshMeetings,
        refreshClassrooms,
        refreshEngagement,
        refreshProgress,
        refreshResourceLibrary
    ]);
};
